<template>
  <div class="flex min-h-0 flex-1 flex-col overflow-hidden">
    <div class="flex items-center justify-between border-b border-white/10 px-5 py-4">
      <div>
        <h2 class="text-sm font-bold">Webinar offer</h2>
        <p class="mt-0.5 text-xs text-zinc-500">Create and push an offer into the live chat</p>
      </div>
      <span
        class="rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider"
        :class="statusClass"
      >
        {{ statusLabel }}
      </span>
    </div>

    <div v-if="loading" class="flex-1 space-y-4 overflow-hidden p-5">
      <div v-for="row in 4" :key="row" class="h-16 animate-pulse rounded-xl bg-white/5" />
      <div class="aspect-video animate-pulse rounded-2xl bg-white/5" />
    </div>

    <div v-else class="offer-scrollbar flex-1 space-y-4 overflow-y-auto p-5">
      <p v-if="error" class="rounded-xl border border-red-400/20 bg-red-500/10 px-3 py-2.5 text-xs leading-5 text-red-200">
        {{ error }}
      </p>

      <Transition name="offer-feedback" mode="out-in">
        <div
          v-if="feedback"
          :key="feedback"
          class="flex items-center gap-2 rounded-xl border border-emerald-400/15 bg-emerald-500/10 px-3 py-2.5 text-xs font-semibold text-emerald-200"
        >
          <CheckCircleIcon class="h-5 w-5 shrink-0" />
          {{ feedback }}
        </div>
      </Transition>

      <fieldset :disabled="isLive || busy" class="space-y-4 disabled:opacity-60">
        <label class="block">
          <span class="text-xs font-medium text-zinc-400">Offer headline</span>
          <input
            v-model.trim="headline"
            type="text"
            maxlength="120"
            class="mt-1.5 block w-full rounded-lg border-white/10 bg-black/30 px-3 py-2.5 text-sm text-zinc-200 placeholder:text-zinc-600 focus:border-blue-500 focus:ring-blue-500"
            placeholder="Get this awesome offer now!"
          />
        </label>

        <div>
          <div class="flex items-center justify-between gap-3">
            <span class="text-xs font-medium text-zinc-400">Offer image</span>
            <button
              type="button"
              class="text-xs font-semibold text-blue-300 transition hover:text-blue-200"
              @click="mediaPickerOpen = true"
            >
              {{ imageMediaFileId ? 'Change image' : 'Upload or choose media' }}
            </button>
          </div>
          <div class="mt-2 overflow-hidden rounded-xl border border-dashed border-white/10 bg-black/20">
            <img v-if="imagePreviewUrl" :src="imagePreviewUrl" alt="Offer preview" class="aspect-video w-full object-contain" />
            <button v-else type="button" class="flex aspect-video w-full flex-col items-center justify-center text-zinc-600 hover:text-zinc-400" @click="mediaPickerOpen = true">
              <PhotoIcon class="h-8 w-8" />
              <span class="mt-2 text-xs font-semibold">Choose an optional offer image</span>
            </button>
          </div>
          <div v-if="imageMediaFileId" class="mt-2 flex items-center justify-between gap-3">
            <label class="inline-flex items-center gap-2 text-xs text-zinc-400">
              <input v-model="imageVisible" type="checkbox" class="rounded border-white/20 bg-black/30 text-blue-500 focus:ring-blue-500" />
              Show image when published
            </label>
            <button type="button" class="text-xs font-semibold text-zinc-500 hover:text-red-300" @click="clearImage">Remove</button>
          </div>
        </div>

        <label class="block">
          <span class="text-xs font-medium text-zinc-400">Button text</span>
          <input
            v-model.trim="buttonText"
            type="text"
            maxlength="40"
            class="mt-1.5 block w-full rounded-lg border-white/10 bg-black/30 px-3 py-2.5 text-sm text-zinc-200 placeholder:text-zinc-600 focus:border-blue-500 focus:ring-blue-500"
            placeholder="Claim this offer"
          />
        </label>

        <label class="block">
          <span class="text-xs font-medium text-zinc-400">Destination URL</span>
          <input
            v-model.trim="destinationUrl"
            type="url"
            class="mt-1.5 block w-full rounded-lg border-white/10 bg-black/30 px-3 py-2.5 text-sm text-zinc-200 placeholder:text-zinc-600 focus:border-blue-500 focus:ring-blue-500"
            placeholder="https://"
          />
        </label>

        <label class="flex items-center gap-2 text-xs text-zinc-400">
          <input v-model="openNewTab" type="checkbox" class="rounded border-white/20 bg-black/30 text-blue-500 focus:ring-blue-500" />
          Open the offer in a new tab
        </label>

        <div class="grid grid-cols-2 gap-3">
          <label class="rounded-xl border border-white/10 bg-black/20 p-3">
            <span class="block text-[11px] font-medium text-zinc-500">Button color</span>
            <input v-model="buttonBackgroundColor" type="color" class="mt-2 h-8 w-full cursor-pointer rounded border-0 bg-transparent" />
          </label>
          <label class="rounded-xl border border-white/10 bg-black/20 p-3">
            <span class="block text-[11px] font-medium text-zinc-500">Text color</span>
            <input v-model="buttonTextColor" type="color" class="mt-2 h-8 w-full cursor-pointer rounded border-0 bg-transparent" />
          </label>
        </div>
      </fieldset>

      <div>
        <p class="mb-2 text-[10px] font-bold uppercase tracking-[.15em] text-blue-400">Member preview</p>
        <div class="relative overflow-hidden rounded-2xl border border-white/10 bg-[#101217] p-2">
          <Transition name="webinar-offer-slide" appear>
            <article v-if="previewVisible" class="overflow-hidden rounded-xl border border-zinc-200 bg-white text-center shadow-2xl">
              <h3 class="px-4 py-3 text-base font-bold text-zinc-800">{{ headline || 'Get this awesome offer now!' }}</h3>
              <img v-if="imageVisible && imagePreviewUrl" :src="imagePreviewUrl" alt="Offer" class="aspect-video w-full bg-zinc-50 object-contain" />
              <button
                type="button"
                class="w-full px-4 py-3 text-sm font-bold shadow-sm"
                :style="{ backgroundColor: buttonBackgroundColor, color: buttonTextColor }"
              >
                {{ buttonText || 'Claim this offer' }}
              </button>
            </article>
          </Transition>
        </div>
      </div>

      <p v-if="isLive" class="rounded-xl border border-amber-400/10 bg-amber-500/5 p-3 text-xs leading-5 text-amber-200/80">
        This offer is live for members. Remove it before changing the offer details.
      </p>

      <div class="grid gap-2 pb-2">
        <button
          v-if="!isLive"
          type="button"
          class="rounded-xl border border-white/10 px-4 py-3 text-sm font-semibold text-zinc-300 transition hover:bg-white/5 disabled:cursor-wait disabled:opacity-50"
          :disabled="busy"
          @click="saveDraft"
        >
          {{ busy ? 'Saving…' : 'Save offer draft' }}
        </button>
        <button
          type="button"
          class="inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-bold text-white shadow-lg transition disabled:cursor-wait disabled:opacity-50"
          :class="isLive ? 'bg-red-500 hover:bg-red-600' : 'bg-[#5db851] hover:bg-emerald-600'"
          :disabled="busy"
          @click="isLive ? removeOffer() : publishOffer()"
        >
          <ArrowPathIcon v-if="busy" class="h-5 w-5 animate-spin" />
          <SignalIcon v-else-if="!isLive" class="h-5 w-5" />
          <XMarkIcon v-else class="h-5 w-5" />
          {{ isLive ? 'Remove and edit this offer' : 'Push this offer LIVE NOW' }}
        </button>
      </div>
    </div>

    <WebinarMediaPickerModal
      :show="mediaPickerOpen"
      media-type="image"
      title="Choose an offer image"
      upload-origin="creator-webinar-offer-image"
      z-index-class="z-[2200]"
      @close="mediaPickerOpen = false"
      @select="applyImage"
    />
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  PhotoIcon,
  SignalIcon,
  XMarkIcon,
} from '@heroicons/vue/24/outline';
import WebinarMediaPickerModal from '@/Components/WebinarMediaPickerModal.vue';
import { useWebinarLiveStore } from '@/stores/webinarLiveStore';

const props = defineProps({ eventId: { type: Number, required: true } });
const store = useWebinarLiveStore();
const apiProxyUrl = String(process.env.VUE_APP_API_PROXY_URL || '').replace(/\/$/, '');
const loading = ref(true);
const localBusy = ref(false);
const error = ref('');
const feedback = ref('');
const status = ref('draft');
const headline = ref('');
const imageMediaFileId = ref('');
const imageSelectedPreview = ref('');
const imageVisible = ref(true);
const buttonText = ref('');
const destinationUrl = ref('');
const openNewTab = ref(true);
const buttonBackgroundColor = ref('#2d8bff');
const buttonTextColor = ref('#ffffff');
const mediaPickerOpen = ref(false);
let feedbackTimer = null;

const busy = computed(() => localBusy.value || store.offerBusy);
const isLive = computed(() => status.value === 'live');
const previewVisible = computed(() => Boolean(headline.value || buttonText.value || imageMediaFileId.value));
const imagePreviewUrl = computed(() => {
  if (imageSelectedPreview.value) return imageSelectedPreview.value;
  return imageMediaFileId.value
    ? `${apiProxyUrl}/api/image/${encodeURIComponent(imageMediaFileId.value)}`
    : '';
});
const statusLabel = computed(() => isLive.value ? 'Live' : status.value === 'hidden' ? 'Removed' : 'Draft');
const statusClass = computed(() => (
  isLive.value
    ? 'bg-emerald-400/10 text-emerald-300'
    : status.value === 'hidden'
      ? 'bg-zinc-400/10 text-zinc-400'
      : 'bg-amber-400/10 text-amber-300'
));

function offerPayload() {
  return {
    headline: headline.value,
    imageMediaFileId: imageMediaFileId.value || null,
    imageVisible: imageVisible.value,
    buttonText: buttonText.value,
    destinationUrl: destinationUrl.value,
    openNewTab: openNewTab.value,
    buttonBackgroundColor: buttonBackgroundColor.value,
    buttonTextColor: buttonTextColor.value,
  };
}

function hydrate(offer) {
  if (!offer) return;
  headline.value = String(offer.headline || '');
  imageMediaFileId.value = String(offer.imageMediaFileId || '');
  imageSelectedPreview.value = '';
  imageVisible.value = offer.imageVisible !== false;
  buttonText.value = String(offer.buttonText || '');
  destinationUrl.value = String(offer.destinationUrl || '');
  openNewTab.value = offer.openNewTab !== false;
  buttonBackgroundColor.value = String(offer.buttonBackgroundColor || '#2d8bff');
  buttonTextColor.value = String(offer.buttonTextColor || '#ffffff');
  status.value = String(offer.status || 'draft');
}

function showFeedback(message) {
  feedback.value = message;
  if (feedbackTimer) window.clearTimeout(feedbackTimer);
  feedbackTimer = window.setTimeout(() => { feedback.value = ''; }, 3500);
}

function validatePublish() {
  if (!headline.value || !buttonText.value || !destinationUrl.value) {
    error.value = 'Add an offer headline, button text, and destination URL before publishing.';
    return false;
  }
  try {
    const parsed = new URL(destinationUrl.value);
    if (!['http:', 'https:'].includes(parsed.protocol)) throw new Error();
  } catch (_validationError) {
    error.value = 'Enter a complete offer URL beginning with https:// or http://.';
    return false;
  }
  return true;
}

async function saveDraft() {
  localBusy.value = true;
  error.value = '';
  try {
    const offer = await store.saveOffer(props.eventId, offerPayload());
    hydrate(offer);
    showFeedback('Offer draft saved');
  } catch (requestError) {
    error.value = requestError?.message || 'Unable to save this offer.';
  } finally {
    localBusy.value = false;
  }
}

async function publishOffer() {
  error.value = '';
  if (!validatePublish()) return;
  localBusy.value = true;
  try {
    const offer = await store.publishOffer(props.eventId, offerPayload());
    hydrate(offer);
    showFeedback('Your offer is live now in the chat');
  } catch (requestError) {
    error.value = requestError?.message || 'Unable to publish this offer.';
  } finally {
    localBusy.value = false;
  }
}

async function removeOffer() {
  localBusy.value = true;
  error.value = '';
  try {
    const offer = await store.hideOffer(props.eventId);
    hydrate(offer);
    showFeedback('Offer removed from the live chat');
  } catch (requestError) {
    error.value = requestError?.message || 'Unable to remove this offer.';
  } finally {
    localBusy.value = false;
  }
}

function applyImage(media) {
  imageMediaFileId.value = String(media?.fileId || '');
  imageSelectedPreview.value = String(media?.previewUrl || '');
  imageVisible.value = true;
  mediaPickerOpen.value = false;
}

function clearImage() {
  imageMediaFileId.value = '';
  imageSelectedPreview.value = '';
}

onMounted(async () => {
  try {
    hydrate(await store.fetchOffer(props.eventId));
  } catch (requestError) {
    error.value = requestError?.message || 'Unable to load this Webinar offer.';
  } finally {
    loading.value = false;
  }
});

onBeforeUnmount(() => {
  if (feedbackTimer) window.clearTimeout(feedbackTimer);
});
</script>

<style scoped>
.webinar-offer-slide-enter-active,
.webinar-offer-slide-leave-active {
  transition: transform 650ms cubic-bezier(.22, 1, .36, 1), opacity 400ms ease;
}
.webinar-offer-slide-enter-from,
.webinar-offer-slide-leave-to {
  transform: translateY(-105%);
  opacity: 0;
}
.offer-feedback-enter-active,
.offer-feedback-leave-active {
  transition: opacity 180ms ease, transform 180ms ease;
}
.offer-feedback-enter-from,
.offer-feedback-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}
.offer-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: rgb(82 82 91 / 70%) transparent;
}
.offer-scrollbar::-webkit-scrollbar { width: 5px; }
.offer-scrollbar::-webkit-scrollbar-track { background: transparent; }
.offer-scrollbar::-webkit-scrollbar-thumb { border-radius: 999px; background: rgb(82 82 91 / 70%); }
</style>
